import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

/**
 * Which weight-to-volume rate (USG per MT) converts which part of a bid, as the backend's
 * common/components/unit_rates/conversion.py (decisions #45-#47). The limits come from
 * /system/unit-conversion/; the tables themselves from /system/unit-rates/?w_to_volume=.
 *
 * - Jet A-1: the bid's own value, else the tender's (the resolver's table), else 331.
 * - SAF: the bid's own value, else the SAF default (342.2).
 * - SAF fee: the blend's rate, 1 / ((1 - b) / jet + b / saf); the SAF rate without a blend.
 * A value outside its range is ignored (an old density such as 0.8 never scales a price).
 *
 * Bids may be classic models (camelCase) or raw payloads (snake_case); both are read.
 */
export interface RateLimit { default: number; min: number; max: number; }
export interface RateLimits { jet_a1: RateLimit; saf: RateLimit; }

/** What loadBidRateTables needs from UnitRatesService. */
export interface UnitRatesSource {
    fetchConversion(): Observable<any>;
    fetchAll(params?: {}): Observable<any>;
}

function read(bid: any, camel: string, snake: string): any {
    if (!bid) {
        return undefined;
    }
    return bid[camel] ?? bid[snake];
}

export function inRange(value: unknown, limit: RateLimit): number | null {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const rate = Number(value);
    return Number.isFinite(rate) && rate >= limit.min && rate <= limit.max ? rate : null;
}

export function bidJetRate(bid: any, tenderRate: number, limits: RateLimits): number {
    return inRange(read(bid, 'weightToVolume', 'weight_to_volume'), limits.jet_a1) ?? tenderRate;
}

/** The backend's Jet A-1 limits (common/constants), used when /system/unit-conversion/ fails. */
export const JET_A1_FALLBACK: RateLimit = { default: 331, min: 300, max: 400 };

/**
 * `weight_to_volume` in a formula context (D-046, D-517): the bid's own rate, else the
 * tender's agreed rate, else 331 -- as bid_total.py gives it. Never the raw bid field, which
 * is empty on most bids and made every "331 / weight_to_volume" term 0.
 */
export function formulaWeightToVolume(bid: any, tenderRate: number | null | undefined,
                                      limits: RateLimits | null | undefined): number {
    const jetLimits = limits?.jet_a1 || JET_A1_FALLBACK;
    const tender = inRange(tenderRate, jetLimits) ?? jetLimits.default;
    return inRange(read(bid, 'weightToVolume', 'weight_to_volume'), jetLimits) ?? tender;
}

/** The agreed rate a unit-rate table was loaded at (its MT -> USG), or undefined. */
export function tableUsgPerMt(table: any): number | undefined {
    return Number(table?.['MT']?.['USG']) || undefined;
}

export function bidSafRate(bid: any, limits: RateLimits): number {
    return inRange(read(bid, 'safWeightToVolume', 'saf_weight_to_volume'), limits.saf) ?? limits.saf.default;
}

export function blendedRate(jet: number, saf: number, blendPercent: unknown): number {
    const b = Number(blendPercent) / 100;
    if (!Number.isFinite(b) || b <= 0) {
        return jet;
    }
    if (b >= 1) {
        return saf;
    }
    return 1 / ((1 - b) / jet + b / saf);
}

export function safFeeRate(bid: any, jet: number, saf: number): number {
    const raw = read(bid, 'safMandateBlend', 'saf_mandate_blend');
    const blend = raw === null || raw === undefined || raw === '' ? NaN : Number(raw);
    return Number.isFinite(blend) && blend > 0 ? blendedRate(jet, saf, blend) : saf;
}

/** The neat SAF price a bid is priced on: the mandated one (what suppliers now enter), else
 * the legacy neat_saf, as bid_total._saf_rows. Null when neither carries a value. */
export function neatSafPriceOf(bid: any): any {
    const priced = (p: any) => p && p.price && p.price.value !== null && p.price.value !== undefined
        && p.price.value !== '' ? p : null;
    return priced(read(bid, 'neatSafMandated', 'neat_saf_mandated')) || priced(read(bid, 'neatSaf', 'neat_saf'));
}

/** Every rate the bids need a table for, other than the tender's own (already loaded). */
export function ratesNeeded(bids: any[], tenderRate: number, limits: RateLimits): number[] {
    const rates = new Set<number>();
    for (const bid of bids) {
        if (!bid) {
            continue;
        }
        const jet = bidJetRate(bid, tenderRate, limits);
        const saf = bidSafRate(bid, limits);
        [jet, saf, safFeeRate(bid, jet, saf)].forEach(rate => rates.add(rate));
    }
    rates.delete(tenderRate);
    return [...rates];
}

/**
 * The unit-rate tables a page converts bids with: the tender's (the route resolver loaded it
 * at the tender's rate) plus one per other rate a bid needs. Without the limits every part
 * uses the tender's table.
 */
export class BidRateTables {
    private readonly byRate: { [rate: number]: any } = {};

    constructor(public readonly tenderTable: any, public readonly limits: RateLimits | null) {
    }

    /** The tender's agreed rate: its table's MT -> USG. */
    public tenderRate(): number {
        return Number(this.tenderTable?.['MT']?.['USG']) || this.limits?.jet_a1?.default;
    }

    public add(rate: number, table: any): void {
        if (table) {
            this.byRate[rate] = table;
        }
    }

    public tableAt(rate: number): any {
        if (!rate || rate === this.tenderRate()) {
            return this.tenderTable;
        }
        return this.byRate[rate] || this.tenderTable;
    }

    public jetRate(bid: any): number {
        return this.limits ? bidJetRate(bid, this.tenderRate(), this.limits) : this.tenderRate();
    }

    /** Tables for one bid: Jet A-1 (differential, fees, market price, index), SAF (neat SAF
     * prices) and the SAF fee's blend. */
    public ofBid(bid: any): { jet: any; saf: any; safFee: any } {
        if (!this.limits) {
            return { jet: this.tenderTable, saf: this.tenderTable, safFee: this.tenderTable };
        }
        const jet = bidJetRate(bid, this.tenderRate(), this.limits);
        const saf = bidSafRate(bid, this.limits);
        return { jet: this.tableAt(jet), saf: this.tableAt(saf), safFee: this.tableAt(safFeeRate(bid, jet, saf)) };
    }

    public missingRates(bids: any[]): number[] {
        if (!this.limits) {
            return [];
        }
        return ratesNeeded(bids, this.tenderRate(), this.limits).filter(rate => !this.byRate[rate]);
    }
}

/** Loads the limits and every table `bids` need beyond the tender's. Never fails: without
 * the limits or a table the tender's table is used. */
export function loadBidRateTables(source: UnitRatesSource, tenderTable: any, bids: any[]): Observable<BidRateTables> {
    return source.fetchConversion().pipe(
        catchError(() => of(null)),
        switchMap((limits: RateLimits | null) => {
            const tables = new BidRateTables(tenderTable, limits);
            const rates = tables.missingRates(bids || []);
            if (!rates.length) {
                return of(tables);
            }
            return forkJoin(rates.map(rate => source.fetchAll({ w_to_volume: rate }).pipe(
                map(table => ({ rate, table })), catchError(() => of(null))))).pipe(
                map(results => {
                    (results || []).filter(Boolean).forEach(({ rate, table }) => tables.add(rate, table));
                    return tables;
                }));
        }),
    );
}
